import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { formatXOF, BACKEND_URL } from "../lib/api";
import { isMobile } from "../lib/device";
import { WAVE_LOGO_URL, ORANGE_MONEY_LOGO_URL } from "../lib/payment-logos";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Loader2, Crown, Users, Coins, TrendingUp, Calendar, Copy, QrCode, MessageCircle, Play, Pause, Download, UserPlus, X, AlertCircle, CheckCircle2, Clock, BadgeAlert, FileText, ShieldCheck, Send, ShieldPlus, ShieldMinus, XCircle, Smartphone } from "lucide-react";

const STATUS_BADGES = {
  paid: { label: "Payé", icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 border-0" },
  pending: { label: "En attente", icon: Clock, color: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 border-0" },
  late: { label: "En retard", icon: BadgeAlert, color: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300 border-0" },
};

export default function TontineDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [tontine, setTontine] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [payments, setPayments] = useState([]);
  const [myPayments, setMyPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Add participant form
  const [pName, setPName] = useState("");
  const [pEmail, setPEmail] = useState("");
  const [pPhone, setPPhone] = useState("");

  // Payment popins
  const [resultDialog, setResultDialog] = useState({ open: false, status: null, amount: null });
  const [orangeDialog, setOrangeDialog] = useState({ open: false, qrCode: null, deepLink: null, webLink: null, paymentId: null, status: "pending" });

  const load = async () => {
    try {
      const [t, ps] = await Promise.all([
        api.get(`/tontines/${id}`),
        api.get(`/tontines/${id}/participants`),
      ]);
      setTontine(t.data);
      setParticipants(ps.data);
      if (t.data.role === "organizer") {
        const [py, st] = await Promise.all([
          api.get(`/tontines/${id}/payments`),
          api.get(`/tontines/${id}/stats`),
        ]);
        setPayments(py.data);
        setStats(st.data);
      } else if (t.data.role) {
        const mp = await api.get(`/tontines/${id}/my-payments`);
        setMyPayments(mp.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Tontine introuvable");
      navigate("/tontines");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  // Handle return from payment (Wave redirect → popin on same page)
  useEffect(() => {
    const pid = params.get("pid");
    const paid = params.get("paid");
    const cancelled = params.get("cancelled");
    if (paid && pid) {
      api.get(`/tontine-payments/${pid}/confirm`).then(({ data }) => {
        if (data.status === "paid") {
          setResultDialog({ open: true, status: "success", amount: data.total });
          load();
        } else {
          setResultDialog({ open: true, status: "pending", amount: data.total });
        }
      }).catch(() => {
        setResultDialog({ open: true, status: "failed", amount: null });
      });
      params.delete("paid"); params.delete("pid"); setParams(params, { replace: true });
    } else if (cancelled) {
      setResultDialog({ open: true, status: "cancelled", amount: null });
      params.delete("cancelled"); params.delete("pid"); setParams(params, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  // Poll Orange status while QR dialog open
  useEffect(() => {
    if (!orangeDialog.open || !orangeDialog.paymentId) return;
    if (orangeDialog.status !== "pending") return;
    const tid = setInterval(async () => {
      try {
        const { data } = await api.get(`/tontine-payments/${orangeDialog.paymentId}/confirm`);
        if (data.status === "paid") {
          setOrangeDialog((d) => ({ ...d, open: false, status: "paid" }));
          setResultDialog({ open: true, status: "success", amount: data.total });
          load();
        }
      } catch {}
    }, 5000);
    return () => clearInterval(tid);
  }, [orangeDialog.open, orangeDialog.paymentId, orangeDialog.status]);

  const addParticipant = async (e) => {
    e.preventDefault();
    if (!pEmail && !pPhone) { toast.error("Email ou téléphone requis"); return; }
    try {
      await api.post(`/tontines/${id}/participants`, { name: pName, email: pEmail || null, phone: pPhone || null });
      toast.success("Participant ajouté");
      setPName(""); setPEmail(""); setPPhone("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const removeParticipant = async (pid) => {
    try {
      await api.delete(`/tontines/${id}/participants/${pid}`);
      toast.success("Participant exclu");
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  const togglePromote = async (pid) => {
    try {
      const { data } = await api.post(`/tontines/${id}/participants/${pid}/promote`);
      toast.success(data.role === "co_organizer" ? "Promu co-organisateur" : "Rétrogradé participant");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const startTontine = async () => {
    try {
      await api.post(`/tontines/${id}/start`);
      toast.success("Tontine lancée !");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const togglePause = async () => {
    try {
      const endpoint = tontine.status === "paused" ? "resume" : "pause";
      await api.post(`/tontines/${id}/${endpoint}`);
      toast.success(endpoint === "pause" ? "Tontine suspendue" : "Tontine reprise");
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  const payRound = async (roundNumber, provider) => {
    try {
      const { data } = await api.post(`/tontines/${id}/pay`, { round_number: roundNumber, provider });
      if (provider === "wave" && data.payment_url) {
        window.location.href = data.payment_url;
      } else if (provider === "orange" && (data.qr_code || data.deep_link)) {
        setOrangeDialog({
          open: true,
          qrCode: data.qr_code,
          deepLink: data.deep_link,
          webLink: data.web_link,
          paymentId: data.payment_id,
          status: "pending",
        });
      } else {
        toast.error("Réponse de paiement invalide");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur paiement");
    }
  };

  const downloadCsv = async () => {
    await downloadBlob(`/tontines/${id}/export.csv`, `sen-natt-${id}.csv`);
  };

  const downloadBlob = async (path, filename) => {
    try {
      const token = localStorage.getItem("sd_token");
      const resp = await fetch(`${BACKEND_URL}/api${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) {
        const txt = await resp.text();
        toast.error(JSON.parse(txt).detail || "Erreur");
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Erreur téléchargement");
    }
  };

  const copyInviteLink = () => {
    const link = `${window.location.origin}/tontines/join/${tontine.invite_token}`;
    navigator.clipboard.writeText(link);
    toast.success("Lien copié !");
  };

  const shareWhatsApp = () => {
    const link = `${window.location.origin}/tontines/join/${tontine.invite_token}`;
    const text = `Je vous invite à rejoindre ma tontine « ${tontine.name} » sur Sen Natt : ${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  if (!tontine) return null;

  const isOrganizer = tontine.role === "organizer";
  const isCoOrganizer = tontine.role === "co_organizer";
  const isAdmin = isOrganizer || isCoOrganizer;
  const isParticipant = !!tontine.role;
  const pct = tontine.total_target ? Math.min(100, Math.round((tontine.total_collected / tontine.total_target) * 100)) : 0;
  const statusMap = {
    draft: { label: "Brouillon", color: "bg-zinc-200 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300" },
    active: { label: "Active", color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" },
    paused: { label: "En pause", color: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300" },
    completed: { label: "Terminée", color: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300" },
  };
  const st = statusMap[tontine.status] || statusMap.draft;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12" data-testid="tontine-detail-page">
      {/* Header */}
      <div className="card-soft p-6 sm:p-8 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <code className="text-xs sm:text-sm font-mono bg-secondary px-2 py-1 rounded">{tontine.id}</code>
              <code className="text-xs font-mono text-muted-foreground">#{tontine.short_code}</code>
              <Badge className={`${st.color} border-0`} data-testid="tontine-status-badge">{st.label}</Badge>
              {isOrganizer && <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300 border-0 flex items-center gap-1"><Crown className="h-3 w-3" /> Organisateur</Badge>}
            {isCoOrganizer && <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300 border-0 flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Co-organisateur</Badge>}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-2">{tontine.name}</h1>
            {tontine.description && <p className="text-muted-foreground">{tontine.description}</p>}
          </div>
          {isOrganizer && (
            <div className="flex flex-wrap gap-2">
              {tontine.status === "draft" && participants.length >= 2 && (
                <Button onClick={startTontine} className="btn-gradient rounded-full" data-testid="start-tontine"><Play className="h-4 w-4 mr-1" /> Lancer</Button>
              )}
              {tontine.status === "active" && <Button onClick={togglePause} variant="outline" className="rounded-full" data-testid="pause-tontine"><Pause className="h-4 w-4 mr-1" /> Pause</Button>}
              {tontine.status === "paused" && <Button onClick={togglePause} className="btn-gradient rounded-full" data-testid="resume-tontine"><Play className="h-4 w-4 mr-1" /> Reprendre</Button>}
              <Button onClick={downloadCsv} variant="outline" className="rounded-full" data-testid="download-csv"><Download className="h-4 w-4 mr-1" /> CSV</Button>
              <Button onClick={() => downloadBlob(`/tontines/${id}/bilan.pdf`, `bilan-${id}.pdf`)} variant="outline" className="rounded-full" data-testid="download-bilan-pdf"><FileText className="h-4 w-4 mr-1" /> Bilan PDF</Button>
            </div>
          )}
          {isCoOrganizer && (
            <div className="flex flex-wrap gap-2">
              <Button onClick={downloadCsv} variant="outline" className="rounded-full" data-testid="download-csv-co"><Download className="h-4 w-4 mr-1" /> CSV</Button>
              <Button onClick={() => downloadBlob(`/tontines/${id}/bilan.pdf`, `bilan-${id}.pdf`)} variant="outline" className="rounded-full" data-testid="download-bilan-pdf-co"><FileText className="h-4 w-4 mr-1" /> Bilan PDF</Button>
            </div>
          )}
        </div>

        {/* Progress */}
        <div className="mt-6">
          <div className="flex items-baseline justify-between mb-2">
            <span className="font-display text-2xl font-bold gradient-text">{formatXOF(tontine.total_collected)}</span>
            <span className="text-sm text-muted-foreground">/ {formatXOF(tontine.total_target)}</span>
          </div>
          <div className="h-2 bg-secondary rounded-full overflow-hidden">
            <div className="h-full gradient-gold-green transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {tontine.participants_count}/{tontine.max_participants} participants</span>
            <span className="flex items-center gap-1"><Coins className="h-3 w-3" /> {formatXOF(tontine.contribution_amount)} / tour</span>
            <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {tontine.frequency === "daily" ? "Quotidienne" : tontine.frequency === "weekly" ? "Hebdomadaire" : tontine.frequency === "monthly" ? "Mensuelle" : `Tous les ${tontine.frequency_days}j`}</span>
            {tontine.penalty_percent > 0 && <span className="flex items-center gap-1"><AlertCircle className="h-3 w-3 text-amber-500" /> Pénalité retard {tontine.penalty_percent}%</span>}
          </div>
        </div>
      </div>

      {/* Stats (organizer only) */}
      {isOrganizer && stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="tontine-stats">
          <div className="card-soft p-4">
            <div className="text-xs text-muted-foreground mb-1">Taux participation</div>
            <div className="font-display text-2xl font-bold">{stats.participation_rate}%</div>
          </div>
          <div className="card-soft p-4">
            <div className="text-xs text-muted-foreground mb-1">Cotisations payées</div>
            <div className="font-display text-2xl font-bold text-emerald-600">{stats.paid_count}</div>
          </div>
          <div className="card-soft p-4">
            <div className="text-xs text-muted-foreground mb-1">En retard</div>
            <div className="font-display text-2xl font-bold text-red-600">{stats.late_count}</div>
          </div>
          <div className="card-soft p-4">
            <div className="text-xs text-muted-foreground mb-1">Commissions reçues</div>
            <div className="font-display text-2xl font-bold gradient-text">{formatXOF(stats.total_commission)}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue={isAdmin ? "overview" : "my-payments"}>
        <TabsList className="rounded-full mb-6 flex-wrap h-auto">
          {isAdmin && <TabsTrigger value="overview" className="rounded-full" data-testid="tab-overview">Vue d'ensemble</TabsTrigger>}
          <TabsTrigger value="participants" className="rounded-full" data-testid="tab-participants">Participants</TabsTrigger>
          {isAdmin && <TabsTrigger value="payments" className="rounded-full" data-testid="tab-payments">Cotisations</TabsTrigger>}
          {isParticipant && !isAdmin && <TabsTrigger value="my-payments" className="rounded-full" data-testid="tab-my-payments">Mes cotisations</TabsTrigger>}
          {isParticipant && <TabsTrigger value="chat" className="rounded-full" data-testid="tab-chat">Discussion</TabsTrigger>}
          <TabsTrigger value="share" className="rounded-full" data-testid="tab-share">Inviter</TabsTrigger>
        </TabsList>

        {isOrganizer && (
          <TabsContent value="overview">
            <div className="card-soft p-6 sm:p-8">
              <h3 className="font-display font-semibold text-lg mb-4">Prochaine échéance</h3>
              {stats?.next_due_date ? (
                <p className="text-3xl font-display font-bold gradient-text">{new Date(stats.next_due_date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
              ) : (
                <p className="text-muted-foreground">Aucune échéance à venir</p>
              )}
              <div className="grid sm:grid-cols-3 gap-4 mt-6">
                <div>
                  <div className="text-xs text-muted-foreground">Total attendu</div>
                  <div className="text-xl font-display font-bold">{formatXOF(stats?.expected_total || 0)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Pénalités collectées</div>
                  <div className="text-xl font-display font-bold text-amber-600">{formatXOF(stats?.total_penalties || 0)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">En attente</div>
                  <div className="text-xl font-display font-bold">{stats?.pending_count || 0}</div>
                </div>
              </div>
              <div className="mt-6 p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 text-sm flex items-start gap-3">
                <Clock className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-emerald-900 dark:text-emerald-200">Relances automatiques activées</p>
                  <p className="text-emerald-800 dark:text-emerald-300 mt-1 text-xs">
                    {(tontine.reminder_days_before || []).length > 0 ? `Emails envoyés ${(tontine.reminder_days_before || []).map(d => d === 0 ? "le jour J" : `J-${d}`).join(", ")}.` : "Aucune relance configurée."}
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>
        )}

        {/* Participants */}
        <TabsContent value="participants">
          <div className="card-soft divide-y">
            {participants.map((p) => (
              <div key={p.id} className="p-4 flex items-center gap-3" data-testid={`participant-${p.id}`}>
                <Avatar className="h-10 w-10"><AvatarFallback className="gradient-gold-green text-white">{p.name?.[0]?.toUpperCase()}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold truncate">{p.name}</span>
                    {p.role === "organizer" && <Crown className="h-3 w-3 text-amber-500" />}
                    {p.role === "co_organizer" && <ShieldCheck className="h-3 w-3 text-blue-500" />}
                    {p.status === "excluded" && <Badge variant="destructive" className="text-xs">Exclu</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{p.email || p.phone}</div>
                </div>
                {isOrganizer && p.role !== "organizer" && p.status === "active" && (
                  <Button size="icon" variant="ghost" className="rounded-full h-8 w-8" onClick={() => togglePromote(p.id)} title={p.role === "co_organizer" ? "Rétrograder" : "Promouvoir co-organisateur"} data-testid={`promote-${p.id}`}>
                    {p.role === "co_organizer" ? <ShieldMinus className="h-4 w-4 text-muted-foreground" /> : <ShieldPlus className="h-4 w-4 text-blue-500" />}
                  </Button>
                )}
                {isAdmin && p.role !== "organizer" && p.status === "active" && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="icon" variant="ghost" className="rounded-full h-8 w-8 text-destructive" data-testid={`remove-${p.id}`}><X className="h-4 w-4" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Exclure {p.name} ?</AlertDialogTitle><AlertDialogDescription>L'historique sera conservé mais cette personne ne pourra plus cotiser.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={() => removeParticipant(p.id)} className="bg-destructive">Exclure</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            ))}
            {isAdmin && tontine.status !== "completed" && participants.filter(p => p.status === "active").length < tontine.max_participants && (
              <form onSubmit={addParticipant} className="p-4 bg-secondary/30 space-y-3" data-testid="add-participant-form">
                <div className="font-semibold text-sm flex items-center gap-2"><UserPlus className="h-4 w-4" /> Ajouter un participant</div>
                <div className="grid sm:grid-cols-3 gap-2">
                  <Input placeholder="Nom *" required value={pName} onChange={(e) => setPName(e.target.value)} className="h-10 rounded-xl" data-testid="part-name" />
                  <Input type="email" placeholder="Email" value={pEmail} onChange={(e) => setPEmail(e.target.value)} className="h-10 rounded-xl" data-testid="part-email" />
                  <Input type="tel" placeholder="Téléphone" value={pPhone} onChange={(e) => setPPhone(e.target.value)} className="h-10 rounded-xl" data-testid="part-phone" />
                </div>
                <Button type="submit" className="btn-gradient rounded-full" size="sm" data-testid="add-participant-submit">Ajouter</Button>
              </form>
            )}
          </div>
        </TabsContent>

        {/* Payments (admin = organizer + co-organizer) */}
        {isAdmin && (
          <TabsContent value="payments">
            <PaymentsTable payments={payments} />
          </TabsContent>
        )}

        {/* My payments (participant) */}
        {isParticipant && (
          <TabsContent value="my-payments">
            <MyPayments payments={myPayments} tontine={tontine} onPay={payRound} />
          </TabsContent>
        )}

        {/* Chat */}
        {isParticipant && (
          <TabsContent value="chat">
            <TontineChat tontineId={id} currentUserId={user?.user_id} />
          </TabsContent>
        )}

        {/* Share */}
        <TabsContent value="share">
          <div className="card-soft p-6 sm:p-8 space-y-5">
            <div>
              <Label>Lien d'invitation</Label>
              <div className="flex gap-2 mt-2">
                <Input readOnly value={`${window.location.origin}/tontines/join/${tontine.invite_token}`} className="h-11 rounded-xl font-mono text-xs" />
                <Button onClick={copyInviteLink} variant="outline" className="rounded-xl" data-testid="copy-invite"><Copy className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={shareWhatsApp} variant="outline" className="rounded-full" data-testid="share-wa"><MessageCircle className="h-4 w-4 mr-2" /> WhatsApp</Button>
              <Dialog>
                <DialogTrigger asChild><Button variant="outline" className="rounded-full" data-testid="show-qr-tontine"><QrCode className="h-4 w-4 mr-2" /> QR Code</Button></DialogTrigger>
                <DialogContent className="max-w-sm">
                  <DialogHeader><DialogTitle>Scanner pour rejoindre</DialogTitle></DialogHeader>
                  <div className="flex justify-center p-6 bg-white rounded-2xl"><img src={`${BACKEND_URL}/api/tontines/${id}/qr`} alt="QR" className="w-64 h-64" /></div>
                </DialogContent>
              </Dialog>
            </div>
            <div className="flex items-start gap-3 p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 text-sm">
              <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
              <p className="text-emerald-900 dark:text-emerald-200">Le lien d'invitation est sécurisé et nominatif à votre tontine. Les rejoignants doivent être connectés pour intégrer la tontine.</p>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Orange Money QR/Deeplink popin — adaptive mobile vs desktop */}
      <Dialog open={orangeDialog.open} onOpenChange={(open) => setOrangeDialog((d) => ({ ...d, open }))}>
        <DialogContent className="max-w-md" data-testid="orange-payment-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <img src={ORANGE_MONEY_LOGO_URL} alt="Orange Money" className="h-8 w-8 object-contain rounded-md" />
              Payer la cotisation avec Orange Money
            </DialogTitle>
            <DialogDescription>
              {isMobile()
                ? "Ouvrez Orange Money ou Maxit pour valider votre cotisation."
                : "Scannez ce QR code avec votre téléphone (app Orange Money ou Maxit)."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {isMobile() ? (
              <>
                {orangeDialog.webLink && (
                  <a href={orangeDialog.webLink} className="block">
                    <Button className="w-full btn-gradient rounded-full h-12 text-base" data-testid="orange-deeplink-btn">
                      <Smartphone className="h-5 w-5 mr-2" /> Ouvrir Orange Money / Maxit
                    </Button>
                  </a>
                )}
                {orangeDialog.qrCode && (
                  <details className="text-sm">
                    <summary className="cursor-pointer text-muted-foreground py-2">Ou scannez le QR code depuis un autre appareil</summary>
                    <div className="mt-3 flex justify-center p-3 bg-white rounded-2xl border">
                      <img src={orangeDialog.qrCode} alt="QR Code Orange Money" className="w-48 h-48 object-contain" data-testid="orange-qr-image" />
                    </div>
                  </details>
                )}
              </>
            ) : (
              <>
                {orangeDialog.qrCode && (
                  <div className="flex justify-center p-4 bg-white rounded-2xl border">
                    <img src={orangeDialog.qrCode} alt="QR Code Orange Money" className="w-64 h-64 object-contain" data-testid="orange-qr-image" />
                  </div>
                )}
                <p className="text-sm text-center text-muted-foreground">
                  Ouvrez l'app <strong>Orange Money</strong> ou <strong>Maxit</strong> sur votre téléphone, puis scannez ce QR code.
                </p>
              </>
            )}
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground border-t pt-3">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>En attente de la confirmation du paiement…</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrangeDialog((d) => ({ ...d, open: false }))} className="rounded-full" data-testid="orange-dialog-close">
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment result popin */}
      <Dialog open={resultDialog.open} onOpenChange={(open) => setResultDialog((d) => ({ ...d, open }))}>
        <DialogContent className="max-w-sm" data-testid="payment-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {resultDialog.status === "success" && <CheckCircle2 className="h-6 w-6 text-emerald-600" />}
              {(resultDialog.status === "failed" || resultDialog.status === "cancelled") && <XCircle className="h-6 w-6 text-rose-600" />}
              {resultDialog.status === "pending" && <Loader2 className="h-6 w-6 animate-spin text-amber-600" />}
              {resultDialog.status === "success" && "Cotisation confirmée"}
              {resultDialog.status === "failed" && "Paiement échoué"}
              {resultDialog.status === "cancelled" && "Paiement annulé"}
              {resultDialog.status === "pending" && "Paiement en cours"}
            </DialogTitle>
            <DialogDescription>
              {resultDialog.status === "success" && (
                <>Votre cotisation{resultDialog.amount ? <> de <strong>{formatXOF(resultDialog.amount)}</strong></> : ""} a bien été enregistrée.</>
              )}
              {resultDialog.status === "failed" && "Le paiement n'a pas pu être finalisé. Vous pouvez réessayer."}
              {resultDialog.status === "cancelled" && "Vous avez annulé le paiement. Aucune somme n'a été débitée."}
              {resultDialog.status === "pending" && "Paiement en cours de validation. Cette page se mettra à jour automatiquement."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setResultDialog({ open: false, status: null, amount: null })} className="rounded-full" data-testid="payment-result-close">
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PaymentsTable({ payments }) {
  const downloadReceipt = async (paymentId, participantName, roundNumber) => {
    try {
      const token = localStorage.getItem("sd_token");
      const resp = await fetch(`${BACKEND_URL}/api/tontine-payments/${paymentId}/receipt.pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) { toast.error("Reçu indisponible"); return; }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `recu-tour${roundNumber}-${participantName.replace(/ /g,"_")}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Erreur"); }
  };

  if (!payments.length) return <div className="card-soft p-12 text-center text-muted-foreground">Aucune cotisation pour le moment. Lancez la tontine pour générer les échéances.</div>;
  return (
    <div className="card-soft overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-secondary/50">
          <tr className="text-left">
            <th className="p-3">Tour</th>
            <th className="p-3">Participant</th>
            <th className="p-3">Échéance</th>
            <th className="p-3">Montant</th>
            <th className="p-3">Pénalité</th>
            <th className="p-3">Statut</th>
            <th className="p-3"></th>
          </tr>
        </thead>
        <tbody>
          {payments.map((p) => {
            const s = STATUS_BADGES[p.status] || STATUS_BADGES.pending;
            const Icon = s.icon;
            return (
              <tr key={p.id} className="border-t" data-testid={`payment-row-${p.id}`}>
                <td className="p-3 font-mono">#{p.round_number}</td>
                <td className="p-3">{p.participant_name}</td>
                <td className="p-3 text-xs">{new Date(p.due_date).toLocaleDateString("fr-FR")}</td>
                <td className="p-3 font-semibold">{formatXOF(p.amount)}</td>
                <td className="p-3">{p.penalty ? <span className="text-amber-600 font-semibold">+{formatXOF(p.penalty)}</span> : <span className="text-muted-foreground">—</span>}</td>
                <td className="p-3"><Badge className={`${s.color} text-xs`}><Icon className="h-3 w-3 mr-1" />{s.label}</Badge></td>
                <td className="p-3">
                  {p.status === "paid" && (
                    <Button size="sm" variant="outline" className="rounded-full h-8 text-xs" onClick={() => downloadReceipt(p.id, p.participant_name, p.round_number)} data-testid={`receipt-${p.id}`}>
                      <FileText className="h-3 w-3 mr-1" /> Reçu
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MyPayments({ payments, tontine, onPay }) {
  const [payingRound, setPayingRound] = useState(null);
  const [provider, setProvider] = useState("wave");

  if (!payments.length) return <div className="card-soft p-12 text-center text-muted-foreground">Pas encore d'échéance. La tontine n'est pas encore lancée.</div>;

  const submit = (round) => {
    onPay(round, provider);
    setPayingRound(null);
  };

  return (
    <div className="card-soft divide-y">
      {payments.map((p) => {
        const s = STATUS_BADGES[p.status] || STATUS_BADGES.pending;
        const Icon = s.icon;
        const total = p.amount + Math.round(p.amount * 0.02) + (p.penalty || 0);
        return (
          <div key={p.id} className="p-4 flex items-center gap-4" data-testid={`my-payment-${p.id}`}>
            <div className="font-mono text-sm w-12 shrink-0">#{p.round_number}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">{formatXOF(p.amount)} <span className="text-xs text-muted-foreground font-normal">+ 2% commission{p.penalty ? ` + ${formatXOF(p.penalty)} pénalité` : ""}</span></div>
              <div className="text-xs text-muted-foreground">Échéance : {new Date(p.due_date).toLocaleDateString("fr-FR")}</div>
            </div>
            <Badge className={`${s.color} text-xs`}><Icon className="h-3 w-3 mr-1" />{s.label}</Badge>
            {p.status === "paid" && (
              <Button size="sm" variant="outline" className="rounded-full h-8 text-xs" onClick={async () => {
                const token = localStorage.getItem("sd_token");
                const resp = await fetch(`${BACKEND_URL}/api/tontine-payments/${p.id}/receipt.pdf`, { headers: { Authorization: `Bearer ${token}` } });
                if (!resp.ok) { toast.error("Reçu indisponible"); return; }
                const blob = await resp.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a"); a.href = url; a.download = `recu-tour${p.round_number}.pdf`; a.click();
                URL.revokeObjectURL(url);
              }} data-testid={`my-receipt-${p.id}`}>
                <FileText className="h-3 w-3 mr-1" /> Reçu
              </Button>
            )}
            {p.status !== "paid" && tontine.status === "active" && (
              <Dialog open={payingRound === p.round_number} onOpenChange={(o) => !o && setPayingRound(null)}>
                <DialogTrigger asChild>
                  <Button size="sm" className="btn-gradient rounded-full" onClick={() => setPayingRound(p.round_number)} data-testid={`pay-round-${p.round_number}`}>
                    Payer {formatXOF(total)}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Cotisation tour #{p.round_number}</DialogTitle></DialogHeader>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between"><span>Cotisation</span><span className="font-semibold">{formatXOF(p.amount)}</span></div>
                    <div className="flex justify-between"><span>Commission Sen Natt (2%)</span><span className="font-semibold">{formatXOF(Math.round(p.amount * 0.02))}</span></div>
                    {p.penalty > 0 && <div className="flex justify-between text-amber-600"><span>Pénalité retard ({tontine.penalty_percent}%)</span><span className="font-semibold">{formatXOF(p.penalty)}</span></div>}
                    <div className="flex justify-between pt-3 border-t font-display font-bold text-lg"><span>Total à payer</span><span className="gradient-text">{formatXOF(total)}</span></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <button type="button" onClick={() => setProvider("wave")} className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition ${provider === "wave" ? "border-[#1DC8FF] bg-[#1DC8FF]/10" : "border-input"}`} data-testid="pay-wave">
                      <img src={WAVE_LOGO_URL} alt="Wave" className="h-10 w-10 object-contain rounded-md" />
                      <span className="text-xs font-semibold">Wave</span>
                    </button>
                    <button type="button" onClick={() => setProvider("orange")} className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition ${provider === "orange" ? "border-[#FF7900] bg-[#FF7900]/10" : "border-input"}`} data-testid="pay-orange">
                      <img src={ORANGE_MONEY_LOGO_URL} alt="Orange Money" className="h-10 w-10 object-contain rounded-md" />
                      <span className="text-xs font-semibold">Orange Money</span>
                    </button>
                  </div>
                  <DialogFooter>
                    <Button onClick={() => submit(p.round_number)} className="btn-gradient rounded-full w-full" data-testid="confirm-pay">Procéder au paiement</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        );
      })}
    </div>
  );
}

function TontineChat({ tontineId, currentUserId }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const fetchMessages = async () => {
    try {
      const { data } = await api.get(`/tontines/${tontineId}/messages`);
      setMessages(data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchMessages();
    const i = setInterval(fetchMessages, 5000);
    return () => clearInterval(i);
    // eslint-disable-next-line
  }, [tontineId]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/tontines/${tontineId}/messages`, { content: text.trim() });
      setMessages((m) => [...m, data]);
      setText("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="card-soft flex flex-col h-[60vh] max-h-[600px]" data-testid="tontine-chat">
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <div className="text-center text-muted-foreground py-12 text-sm">
            <MessageCircle className="h-10 w-10 mx-auto mb-2 opacity-30" />
            <p>Aucun message pour le moment. Lancez la conversation !</p>
          </div>
        )}
        {messages.map((m) => {
          const mine = m.user_id === currentUserId;
          return (
            <div key={m.id} className={`flex gap-2 ${mine ? "flex-row-reverse" : ""}`} data-testid={`msg-${m.id}`}>
              <Avatar className="h-8 w-8 shrink-0"><AvatarFallback className="gradient-gold-green text-white text-xs">{m.author_name?.[0]?.toUpperCase()}</AvatarFallback></Avatar>
              <div className={`max-w-[75%] ${mine ? "items-end" : ""} flex flex-col`}>
                <div className={`px-3 py-2 rounded-2xl text-sm ${mine ? "bg-emerald-600 text-white rounded-br-sm" : "bg-secondary rounded-bl-sm"}`}>
                  {!mine && <div className="text-xs font-semibold mb-0.5 text-amber-600">{m.author_name}</div>}
                  <div className="whitespace-pre-wrap break-words">{m.content}</div>
                </div>
                <div className="text-[10px] text-muted-foreground mt-1 px-1">{new Date(m.created_at).toLocaleString("fr-FR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" })}</div>
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={send} className="border-t p-3 flex gap-2" data-testid="chat-form">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Écrire un message..."
          className="h-10 rounded-full flex-1"
          maxLength={2000}
          data-testid="chat-input"
        />
        <Button type="submit" disabled={sending || !text.trim()} className="btn-gradient rounded-full h-10 w-10 p-0" data-testid="chat-send">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </form>
    </div>
  );
}
